import React, {useState} from "react";
import {Badge, Button, Dropdown, DropdownMenu, DropdownToggle} from "reactstrap";
import IconLabel from "./IconLabel";
import {CheckIcon, FunnelIcon, XIcon} from "./icons";

/**
 * "Filtrar" dropdown: a checkbox per status, each shown as its status badge.
 * The menu stays open while you tick several. Nothing ticked means no filter.
 * "Todas as situações" ticks every option at once; "Limpar filtro" unticks all.
 *
 * `options` lists every status in display order; `node` is how to show it
 * (usually its badge). `selected` holds the codes that are ticked.
 *
 * @template {string} Code
 * @param {{
 *   options: {code: Code, node: React.ReactNode}[],
 *   selected: Code[],
 *   onChange: (selected: Code[]) => void,
 *   label?: string,
 *   allLabel?: string,
 * }} props
 */
const StatusFilter = ({options, selected, onChange, label = "Situação", allLabel = "Todas as situações"}) => {
    const [open, setOpen] = useState(false);

    const toggle = (code) =>
        onChange(
            // keep the display order whatever order they were ticked in
            options.map((o) => o.code).filter((c) => (c === code ? !selected.includes(c) : selected.includes(c)))
        );

    return (
        // `inNavbar` skips Popper (which doesn't place the menu correctly here)
        // and lets Bootstrap's CSS put it right below the button.
        <Dropdown isOpen={open} toggle={() => setOpen((o) => !o)} inNavbar>
            <DropdownToggle color="primary" outline className="d-inline-flex align-items-center gap-2">
                <FunnelIcon />
                Filtrar
                {selected.length > 0 && (
                    <Badge pill color="primary" aria-label={`${selected.length} selecionados`}>
                        {selected.length}
                    </Badge>
                )}
            </DropdownToggle>
            <DropdownMenu role="group" aria-label={`Filtrar por ${label.toLowerCase()}`} style={{minWidth: 220}}>
                <div className="dropdown-header">{label}</div>
                {options.map(({code, node}) => (
                    <label
                        key={code}
                        className="dropdown-item d-flex align-items-center gap-2 mb-0"
                        style={{cursor: "pointer"}}
                    >
                        <input
                            type="checkbox"
                            className="form-check-input m-0"
                            checked={selected.includes(code)}
                            onChange={() => toggle(code)}
                        />
                        {node}
                    </label>
                ))}
                <div className="dropdown-divider" />
                <div className="px-3 pb-1 d-flex flex-column align-items-start gap-1">
                    <Button
                        color="link"
                        size="sm"
                        className="p-0"
                        disabled={selected.length === options.length}
                        onClick={() => onChange(options.map((o) => o.code))}
                    >
                        <IconLabel icon={<CheckIcon />}>{allLabel}</IconLabel>
                    </Button>
                    <Button
                        color="link"
                        size="sm"
                        className="p-0"
                        disabled={selected.length === 0}
                        onClick={() => onChange([])}
                    >
                        <IconLabel icon={<XIcon />}>Limpar filtro</IconLabel>
                    </Button>
                </div>
            </DropdownMenu>
        </Dropdown>
    );
};

export default StatusFilter;
